/**
 * ConfigurationCardsContainer - Horizontal scrollable container for configuration cards
 *
 * Displays configuration cards in a horizontal scrollable layout with auto-centering
 * of the selected configuration. Clicking a card selects it and centers it smoothly.
 * Navigation arrows appear when content is scrollable.
 */

import { Box, HStack, IconButton } from '@chakra-ui/react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Configuration } from '~/types/project';
import { ConfigurationCard } from './ConfigurationCard';

interface ConfigurationCardsContainerProps {
  configurations: Configuration[];
  selectedConfigurationId: string | null;
  onConfigurationSelect: (id: string) => void;
  onConfigurationNameEdit?: (id: string, newName: string) => void;
  onConfigurationDelete?: (id: string) => void;
}

/**
 * Scroll selected card to center of container
 */
const scrollToCenter = (cardRef: HTMLElement, containerRef: HTMLElement) => {
  const cardRect = cardRef.getBoundingClientRect();
  const containerRect = containerRef.getBoundingClientRect();

  const cardCenter = cardRect.left + cardRect.width / 2;
  const containerCenter = containerRect.left + containerRect.width / 2;
  const scrollLeft = containerRef.scrollLeft + (cardCenter - containerCenter);

  containerRef.scrollTo({ left: scrollLeft, behavior: 'smooth' });
};

export const ConfigurationCardsContainer: React.FC<ConfigurationCardsContainerProps> = ({
  configurations,
  selectedConfigurationId,
  onConfigurationSelect,
  onConfigurationNameEdit,
  onConfigurationDelete,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Track scroll state for showing/hiding arrows
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Update scroll state
  const updateScrollState = useCallback(() => {
    if (!containerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = containerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  // Check scroll state on mount and when configurations change
  // biome-ignore lint/correctness/useExhaustiveDependencies: configurations.length triggers recalc when configs change
  useEffect(() => {
    updateScrollState();
    // Also check after a short delay to handle DOM updates
    const timeoutId = setTimeout(updateScrollState, 100);
    return () => clearTimeout(timeoutId);
  }, [configurations.length, updateScrollState]);

  // Scroll by a fixed amount
  const scrollBy = useCallback((direction: 'left' | 'right') => {
    if (!containerRef.current) return;
    const scrollAmount = 300; // pixels to scroll
    const newScrollLeft =
      containerRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
    containerRef.current.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
  }, []);

  // Auto-center selected card when selection changes
  useEffect(() => {
    if (!selectedConfigurationId || !containerRef.current) return;

    const cardRef = cardRefs.current.get(selectedConfigurationId);
    if (!cardRef) return;

    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      scrollToCenter(cardRef, containerRef.current!);
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [selectedConfigurationId]);

  const handleCardClick = (id: string) => {
    onConfigurationSelect(id);
    // Scroll will happen via useEffect
  };

  const handleNameEdit = (id: string, newName: string) => {
    if (onConfigurationNameEdit) {
      onConfigurationNameEdit(id, newName);
    }
  };

  const handleDelete = (id: string) => {
    if (onConfigurationDelete) {
      onConfigurationDelete(id);
    }
  };

  // Sort configurations by createdAt (oldest first) for consistent display
  const sortedConfigurations = [...configurations].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA - dateB;
  });

  // Show arrows only when there are enough configurations to scroll
  const showArrows = configurations.length > 2;

  return (
    <HStack width="100%" gap={2} alignItems="center" mb={4}>
      {/* Left scroll arrow */}
      {showArrows && (
        <IconButton
          aria-label="Scroll left"
          variant="ghost"
          size="sm"
          onClick={() => scrollBy('left')}
          disabled={!canScrollLeft}
          opacity={canScrollLeft ? 1 : 0.3}
          _hover={{ bg: canScrollLeft ? 'gray.100' : 'transparent' }}
          flexShrink={0}
        >
          <ChevronLeft size={20} />
        </IconButton>
      )}

      {/* Scrollable container */}
      <Box
        ref={containerRef}
        overflowX="auto"
        overflowY="hidden"
        whiteSpace="nowrap"
        flex="1"
        p={4}
        onScroll={updateScrollState}
        css={{
          '&::-webkit-scrollbar': {
            height: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '4px',
            '&:hover': {
              background: '#555',
            },
          },
        }}
      >
        <HStack
          display="flex"
          gap={4}
          alignItems="center"
          justifyContent={sortedConfigurations.length === 1 ? 'center' : 'flex-start'}
          minWidth="fit-content"
        >
          {sortedConfigurations.map((configuration) => {
            const isSelected = configuration.id === selectedConfigurationId;
            return (
              <Box
                key={configuration.id}
                ref={(el: HTMLDivElement | null) => {
                  if (el) {
                    cardRefs.current.set(configuration.id, el);
                  } else {
                    cardRefs.current.delete(configuration.id);
                  }
                }}
                display="inline-block"
                whiteSpace="normal"
              >
                <ConfigurationCard
                  configuration={configuration}
                  isSelected={isSelected}
                  onClick={() => handleCardClick(configuration.id)}
                  onNameEdit={
                    onConfigurationNameEdit
                      ? (name) => handleNameEdit(configuration.id, name)
                      : undefined
                  }
                  onDelete={
                    onConfigurationDelete ? () => handleDelete(configuration.id) : undefined
                  }
                />
              </Box>
            );
          })}
        </HStack>
      </Box>

      {/* Right scroll arrow */}
      {showArrows && (
        <IconButton
          aria-label="Scroll right"
          variant="ghost"
          size="sm"
          onClick={() => scrollBy('right')}
          disabled={!canScrollRight}
          opacity={canScrollRight ? 1 : 0.3}
          _hover={{ bg: canScrollRight ? 'gray.100' : 'transparent' }}
          flexShrink={0}
        >
          <ChevronRight size={20} />
        </IconButton>
      )}
    </HStack>
  );
};
